import { ArchiveIcon } from '@heroicons/react/outline';
import * as React from 'react';
import './Table.scss';
import { UseMutationResult } from 'react-query';
import { ReactComponent as NotFound } from '../../assets/img/not-found.svg';
import { ReactComponent as RefreshSmall } from '../../assets/img/refresh-small.svg';
import { useTranslation } from 'react-i18next';
type TableColumn<Entry> = {
  title: string;
  field: keyof Entry;
  width?: number;
  HeaderCell?(): React.ReactElement;
  Cell?({ entry }: { entry: Entry }): React.ReactElement;
  hide?: boolean;
};

export type TableProps<Entry> = {
  data?: Entry[];
  columns: TableColumn<Entry>[];
  tableInitialState: {
    tableTotalRecords: number;
    tablePageIndex?: number;
  };
  isDataLoading?: boolean;
  onTableStateChange?: (pageIndex: number) => any;
};

export const TableData = <Entry extends { id?: string }>({
  data,
  columns,
  tableInitialState,
  isDataLoading,
  onTableStateChange,
}: TableProps<Entry>) => {
  const { t } = useTranslation();
  const [totalData, setTotalData] = React.useState(0);
  const [selectedPage, setSelectedPage] = React.useState(1);
  const [pageIndex, setPageIndex] = React.useState(tableInitialState.tablePageIndex);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < 50 && !isDataLoading) {
        getNextSetOfData();
      }
    }
  };

  const getNextSetOfData = () => {
    const totalRecords = data && data.length ? data.length : 0;
    if (totalRecords < tableInitialState.tableTotalRecords) {
      onTableStateChange && onTableStateChange(selectedPage + 1);
      setPageIndex(selectedPage + 1);
    }
  };

  if (!data?.length && !isDataLoading) {
    return (
      <>
        {window.location.pathname == '/app/events' ? (
          <div className="not-found">
            <div className="n-found-img-outer">
              <NotFound />
            </div>

            <div className="title-text-not-found">{t('You have no appointment yet')}</div>
            <div className="sub-title-text-not-found">
              {t('Share your store link with customers for booking. If they')}
            </div>
            <div className="sub-title-text-not-found">
              {t('already booked, click')}
              <RefreshSmall /> {t('button at the top.')}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-500 bg-white h-80">
            <ArchiveIcon className="w-16 h-16" />
            <h4 className='no-enteries'>{window.location.pathname == '/app/reviews' ? t("No Reviews") : t("No Entries Found")}</h4>
          </div>
        )}
      </>
    );
  }
  return (
    <div
      className="table-container"
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
      ref={tableContainerRef}
    >
      <table className="petsked-table min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            {columns.map(({ HeaderCell, title, width, hide }, index) =>
              hide ? null : (
                <th
                  key={title + index}
                  scope="col"
                  style={{ width: `${width}%`, maxWidth: `${width}%` }}
                  className="px-6 py-3 text-lg font-medium tracking-wider text-left text-gray-500 uppercase"
                >
                  {HeaderCell ? (
                    <span className="d-flex items-center">
                      <HeaderCell />
                      {title}
                    </span>
                  ) : (
                    title
                  )}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((entry, entryIndex) => (
              <tr key={entry?.id || entryIndex} className="odd:bg-white">
                {columns.map(({ Cell, field, title, hide }, columnIndex) =>
                  hide ? null : (
                    <td
                      key={title + columnIndex}
                      className="px-6 py-4 text-lg font-medium text-gray-400 whitespace-nowrap"
                    >
                      {Cell ? <Cell entry={entry} /> : entry[field]}
                    </td>
                  )
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};
